import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import type { CourseLayout, GameView, RobotView, TokenView } from '../api.js';
import type { Shot } from './useSteppedPlayback.js';

/**
 * Draws the course: each board's PNG placed and rotated, with flags,
 * docking bays and robots laid over it.
 *
 * The art is a plain <img> per board rather than one stitched image,
 * because a course is a handful of boards and letting the browser
 * composite them means a board can be swapped without regenerating
 * anything.
 *
 * COORDINATES. Everything is `cell * cellPx`. The board PNGs must be
 * exported with no margin and no offset, or every overlay drifts further
 * out of place the further it is from the origin. See BRINGUP-WINDOWS.md
 * step 5.
 *
 * ROTATION. A board's rotation is applied to the image with a CSS
 * transform about its own centre. Every board is 12x12 except a docking
 * bay, which is 12x4: its image is drawn at its natural size and centred
 * on its footprint, which the server has already transposed for a quarter
 * turn, so the two coincide once it is spun.
 */

interface BoardProps {
  layout: CourseLayout;
  robots: RobotView[];
  /** Robot id to colour, from the game's player list. */
  colours: Map<string, string>;
  /** The viewer's own robot, drawn with a marker so it is findable. */
  youAre: string | null;
  /** What to call each robot: the player's name, not the internal id. */
  nameOf: (robotId: string) => string;
  /** Robots a bot account is playing. Their flag number is red. */
  bots?: Set<string>;
  /** Things dropped on the board. */
  tokens: TokenView[];
  /** The shot to draw between two robots this beat, where one fired. */
  shot?: Shot | null;
  /**
   * Where the flags are NOW, when a belt has carried one.
   *
   * Absent on the courses where flags never move, and `layout.flags` is
   * then what to draw -- which is every course that has no flag standing
   * on a conveyance. A flag marked `lost` is off the board for the rest
   * of the register and is not drawn at all.
   */
  flags?: GameView['flags'];
  /** Robots destroyed during the register just played, drawn struck
   * through where they died. */
  wrecked?: Set<string>;
  /**
   * Where the game has got to, as one value: the turn and register.
   *
   * Squares peeled open to look underneath are closed again whenever this
   * changes, so an inspection lasts as long as the board is still.
   */
  epoch?: string;
  /**
   * Changes when the next positions are a CUT rather than a move.
   *
   * Robot movement is animated, which is right for a register playing out
   * and wrong for jumping into a replay: the board was showing turn 12
   * and is about to show turn 3, and the robots slid the whole way across
   * the course to get there and back again afterwards. Nothing moved --
   * the picture was replaced.
   *
   * Changing this remounts the robots, so the new positions are simply
   * where they are. Leave it alone and they animate, which is what a
   * register should do.
   */
  cut?: string;
  cellPx: number;
}

const FACING_ROTATION: Record<string, number> = { N: 0, E: 90, S: 180, W: 270 };

/** Readable names for the tooltip, where the kind is not obvious. */
const TOKEN_LABEL: Record<string, string> = {
  oil: 'Oil slick',
  flamingOil: 'Flaming oil',
  smoke: 'Smoke',
  water: 'Water',
  mud: 'Mud',
  spike: 'Spikes',
  goo: 'Goo',
  debris: 'Debris',
  mine: 'Mine',
  proximityMine: 'Proximity mine',
  portableTeleporter: 'Portable teleporter',
  scramblerBomb: 'Scrambler bomb',
  seismicCharge: 'Seismic charge',
  missile: 'Missile',
  bridge: 'Bridge',
  radioactiveWaste: 'Radioactive waste',
};

/** Degrees to rotate art drawn pointing north. */
const FACING_TURN: Record<string, number> = { N: 0, E: 90, S: 180, W: 270 };

/** Degrees to rotate a shot, whose art is drawn pointing EAST: the
 * shell's nose is at the right hand end of the tile. */
const SHOT_TURN: Record<string, number> = { E: 0, S: 90, W: 180, N: 270 };

/** Degrees to rotate a Flame Thrower's tile, whose art points NORTH. */
const FLAME_TURN: Record<string, number> = { N: 0, E: 90, S: 180, W: 270 };

export function Board({
  layout, robots, colours, youAre, nameOf, bots, tokens, shot, flags, wrecked, epoch, cut, cellPx,
}: BoardProps) {
  /**
   * Which layer of a square is showing, by square.
   *
   * By DEFAULT a square shows everything on it at once: robot art is
   * mostly hollow, so a token underneath bleeds through and the square
   * reads correctly without any clicking. Clicking then breaks the pile
   * apart -- each robot in turn, then each token in turn, then the bare
   * square -- and round again to everything.
   *
   * Robots are in the cycle because several of them on one square do hide
   * each other, and one at a time is how a pile is read apart.
   */
  const [cycled, setCycled] = useState<Map<string, number>>(new Map());

  /**
   * Everything stacked on each square, grouped so a stack can be drawn
   * and cycled as one thing.
   *
   * Squares with robots but no tokens are included: they still have
   * something worth looking under.
   */
  const stacks = useMemo(() => {
    const bySquare = new Map<string, {
      x: number; y: number; all: TokenView[]; here: string[];
    }>();
    const at = (x: number, y: number) => {
      const key = `${x},${y}`;
      let found = bySquare.get(key);
      if (!found) {
        found = { x, y, all: [], here: [] };
        bySquare.set(key, found);
      }
      return found;
    };
    for (const token of tokens) at(token.x, token.y).all.push(token);
    // The viewer's own robot comes first, so a shared square shows it
    // before anyone else's: a player who cannot see their own robot has
    // nothing to orient by. The rest follow by id, so the cycle order is
    // stable between renders and agreed between clients.
    const order = [...robots].sort((a, b) => {
      if (a.id === youAre) return -1;
      if (b.id === youAre) return 1;
      return a.id.localeCompare(b.id);
    });
    for (const robot of order) {
      if (!robot.destroyed) at(robot.x, robot.y).here.push(robot.id);
    }
    return [...bySquare.values()];
  }, [tokens, robots, youAre]);

  const stackAt = (x: number, y: number) => stacks.find((s) => s.x === x && s.y === y);

  /**
   * One marker per SQUARE rather than per flag.
   *
   * Belts carry flags, so two can end up on one square, and two markers
   * drawn at the same coordinates simply hide each other -- the board
   * would show a flag that is not the one you are looking for. A shared
   * square shows every number on it.
   */
  const flagMarkers = useMemo(() => {
    const bySquare = new Map<string, { x: number; y: number; numbers: number[]; isFinal: boolean }>();
    for (const flag of flags ?? layout.flags) {
      if ('lost' in flag && flag.lost) continue;
      const key = `${flag.x},${flag.y}`;
      const found = bySquare.get(key);
      if (found) {
        found.numbers.push(flag.number);
        // The final flag colours the square however many share it: the
        // one that ends the game is the one worth seeing.
        found.isFinal = found.isFinal || flag.isFinal;
      } else {
        bySquare.set(key, {
          x: flag.x, y: flag.y, numbers: [flag.number], isFinal: flag.isFinal,
        });
      }
    }
    for (const marker of bySquare.values()) marker.numbers.sort((a, b) => a - b);
    return [...bySquare.values()];
  }, [flags, layout.flags]);

  /**
   * What is on each square, as a string, so a change can be noticed.
   *
   * A cycle position is an index into a square's layers, and it is
   * meaningless once those layers are not the same layers. Without this,
   * a square clicked twice early in a game kept that position for the
   * rest of it: robots drove off, others arrived, and the square went on
   * opening at layer 2 of whatever happened to be there.
   */
  const signature = useMemo(() => {
    const byKey = new Map<string, string>();
    for (const s of stacks) {
      byKey.set(`${s.x},${s.y}`, [...s.here, ...s.all.map((t) => t.id)].join('|'));
    }
    return byKey;
  }, [stacks]);

  const lastSignature = useRef(signature);
  useEffect(() => {
    const previous = lastSignature.current;
    lastSignature.current = signature;
    setCycled((current) => {
      if (current.size === 0) return current;
      let next: Map<string, number> | null = null;
      for (const key of current.keys()) {
        if (previous.get(key) !== signature.get(key)) {
          if (!next) next = new Map(current);
          next.delete(key);
        }
      }
      // The same map back when nothing changed: a new one every render
      // would set state forever.
      return next ?? current;
    });
  }, [signature]);

  // And everything closes when the game moves on, whether or not the
  // individual squares changed. A square held open across a register is
  // showing an answer to a question about a board that has since moved.
  useEffect(() => {
    setCycled((current) => (current.size === 0 ? current : new Map()));
  }, [epoch]);

  /**
   * How the cycle position for a square resolves.
   *
   * Which robot and which token are showing both come out of one number,
   * so the token overlay and the robot layer cannot disagree.
   */
  const layerAt = (x: number, y: number) => {
    const stack = stackAt(x, y);
    const here = stack?.here ?? [];
    const all = stack?.all ?? [];
    const count = here.length + all.length;

    // Step 0 is everything at once, then one thing at a time, then bare.
    // A square with only one thing on it has nothing to break apart, so
    // it cycles straight from everything to bare and back.
    const singles = count > 1 ? count : 0;
    const layers = 1 + singles + 1;
    const step = ((cycled.get(`${x},${y}`) ?? 0) % layers + layers) % layers;

    if (step === 0) {
      return {
        everything: true, robotId: null, tokenIndex: -1, bare: false, count,
      };
    }
    // `singles` is 0 for a square holding one thing, so the next step is
    // the bare square. Indexing into the lists regardless of `singles` --
    // which is what this did -- drew that single thing AGAIN as its own
    // layer, so a square with one token cycled from the token to the
    // token and clicking appeared to do nothing at all.
    const pick = step - 1;
    if (pick < singles) {
      if (pick < here.length) {
        return {
          everything: false, robotId: here[pick], tokenIndex: -1, bare: false, count,
        };
      }
      return {
        everything: false, robotId: null, tokenIndex: pick - here.length,
        bare: false, count,
      };
    }
    return { everything: false, robotId: null, tokenIndex: -1, bare: true, count };
  };

  /** Whether this particular robot is drawn right now. */
  const robotShown = (id: string, x: number, y: number) => {
    const stack = stackAt(x, y);
    if (!stack) return true; // nothing recorded here, so nothing to hide behind
    const layer = layerAt(x, y);
    return layer.everything || layer.robotId === id;
  };

  const width = layout.width * cellPx;
  const height = layout.height * cellPx;

  return (
    <div
      className="board"
      // `--cell` is for overlays sized in proportion to a square rather
      // than in fixed units: the lobby draws at 33px and the game at 44.
      style={{ width, height, position: 'relative', '--cell': `${cellPx}px` } as CSSProperties}
      role="img"
      aria-label={`Course ${layout.name}, ${layout.width} by ${layout.height} squares`}
    >
      {layout.boards.map((board, i) => {
        // `cellsWide`/`cellsHigh` are the FOOTPRINT, already transposed by
        // the server for a quarter-turned board. The image itself is still
        // its natural way round, so it is drawn at its natural size and
        // spun about its own centre, which is where CSS rotates from.
        // Placing the centre on the footprint's centre then makes the two
        // coincide. For a square board this reduces to the top-left corner
        // and is exactly what it did before.
        const transposed = board.rotation === 90 || board.rotation === 270;
        const naturalW = transposed ? board.cellsHigh : board.cellsWide;
        const naturalH = transposed ? board.cellsWide : board.cellsHigh;
        const centreX = (board.gridX + board.cellsWide / 2) * cellPx;
        const centreY = (board.gridY + board.cellsHigh / 2) * cellPx;
        return (
          <img
            key={`${board.id}-${i}`}
            src={board.image}
            alt=""
            draggable={false}
            style={{
              position: 'absolute',
              // gridX/gridY are ABSOLUTE CELLS. They used to be lattice
              // slots multiplied by the board size here, which a 12x4
              // docking bay cannot be expressed in at all.
              left: centreX - (naturalW * cellPx) / 2,
              top: centreY - (naturalH * cellPx) / 2,
              width: naturalW * cellPx,
              height: naturalH * cellPx,
              transform: board.rotation ? `rotate(${board.rotation}deg)` : undefined,
              imageRendering: 'auto',
            }}
          />
        );
      })}

      {/* Docking bays. Part of the board art on some boards, but drawn
          here too so a course with no dock still shows where players
          start. */}
      {layout.starts.map((start) => (
        <div
          key={`start-${start.number}`}
          className="bay-marker"
          style={{
            position: 'absolute',
            left: start.x * cellPx,
            top: start.y * cellPx,
            width: cellPx,
            height: cellPx,
          }}
        >
          <span>{start.number}</span>
        </div>
      ))}

      {/* Live positions where a belt has moved a flag, the course's own
          otherwise. A lost flag is off the board until the next register
          and is drawn nowhere; the log is what says where it went.

          Grouped by square, because a belt can carry two onto one and two
          markers in the same place would hide each other -- so a square
          holding flags 1 and 3 reads "1/3". Both are really there: a
          robot scores whichever of them it needs next. */}
      {flagMarkers.map((marker) => (
        <div
          key={`flag-${marker.x},${marker.y}`}
          className={`flag${marker.isFinal ? ' final' : ''}${marker.numbers.length > 1 ? ' shared' : ''}`}
          style={{
            position: 'absolute',
            left: marker.x * cellPx,
            top: marker.y * cellPx,
            width: cellPx,
            height: cellPx,
          }}
          title={marker.numbers.length > 1
            ? `Flags ${marker.numbers.join(' and ')}, sharing a square`
            : marker.isFinal
              ? `Flag ${marker.numbers[0]}, the last one`
              : `Flag ${marker.numbers[0]}`}
        >
          <span>{marker.numbers.join('/')}</span>
        </div>
      ))}

      {/* Tokens: things dropped on the board. Drawn UNDER the robots so a
          robot standing on a mine is still identifiable, and the token is
          still visible around the robot's transparent outline.

          The art is named for the token kind, so no mapping is needed --
          /tokens/oil.png, /tokens/mine.png and so on. A kind with no art
          yet falls back to a label rather than a broken image. */}
      {stacks.map(({ x, y, all }) => {
        // Only one layer is drawn at a time. Clicking advances, so
        // whatever is covered -- a conveyor, a wrench, the robot itself --
        // can be seen. Every square with anything on it is clickable,
        // including one holding only a robot.
        const key = `${x},${y}`;
        const { everything, robotId, tokenIndex, bare, count } = layerAt(x, y);
        const advance = () => setCycled(
          (c) => new Map(c).set(key, (c.get(key) ?? 0) + 1),
        );
        // The badge says there is MORE here than what is showing, so it
        // appears only when there is: a square holding one robot and
        // nothing else, or one token and nothing else, is already fully
        // described by what is drawn. It does appear on the bare layer
        // whatever the count, since something is hidden there by
        // definition.
        // A token always earns the badge: it covers whatever board element
        // is painted under it -- a wrench, a conveyor, a gear -- and the
        // number is the only sign there is something there to look at. A
        // robot on bare floor hides nothing worth saying, so a square with
        // only a robot gets none. The bare layer always carries it.
        const badge = (all.length > 0 || count > 1 || bare)
          ? <span className="token-count">{count}</span>
          : null;

        // Everything at once: every token on the square is drawn, and the
        // robots are drawn over them by the robot layer below. Robot art
        // is mostly hollow, so what is underneath shows through.
        if (everything) {
          return (
            <div
              key={`stack-${key}`}
              className="token token-passthrough"
              style={{ left: x * cellPx, top: y * cellPx, width: cellPx, height: cellPx }}
              onClick={advance}
              role="presentation"
              title={
                count > 1 ? 'Click to look at these one at a time'
                  : 'Click to see the square'
              }
            >
              {badge}
              {all.map((t) => (
                <img
                  key={t.id}
                  className="token-layered"
                  src={`/tokens/${t.kind}.png`}
                  alt={TOKEN_LABEL[t.kind] ?? t.kind}
                  style={{
                    transform: t.facing ? `rotate(${FACING_TURN[t.facing]}deg)` : undefined,
                  }}
                />
              ))}
            </div>
          );
        }

        if (bare || robotId) {
          return (
            <div
              key={`stack-${key}`}
              className={bare ? 'token token-hidden' : 'token token-passthrough'}
              style={{ left: x * cellPx, top: y * cellPx, width: cellPx, height: cellPx }}
              onClick={advance}
              role="presentation"
              title={bare ? 'Show what is here' : 'Click to see what else is here'}
            >
              {badge}
            </div>
          );
        }
        const token = all[tokenIndex];
        return (
        <div
          key={token.id}
          className="token"
          style={{
            left: token.x * cellPx,
            top: token.y * cellPx,
            width: cellPx,
            height: cellPx,
            // A missile points where it is going.
            transform: token.facing ? `rotate(${FACING_TURN[token.facing]}deg)` : undefined,
          }}
          onClick={advance}
          role="presentation"
          title={
            count > 1
              ? `${TOKEN_LABEL[token.kind] ?? token.kind} - click to see the other ${count - 1}`
              : `${TOKEN_LABEL[token.kind] ?? token.kind} - click to see the square`
          }
        >
          {badge}
          {/* The label appears ONLY when the art fails to load -- a kind
              with none yet, or art that was not deployed. Drawing it
              underneath did not work: the icons are transparent, so it
              showed through them. */}
          <img
            src={`/tokens/${token.kind}.png`}
            alt={TOKEN_LABEL[token.kind] ?? token.kind}
            onError={(e) => {
              const img = e.currentTarget;
              img.style.display = 'none';
              const label = img.parentElement?.querySelector('.token-fallback');
              if (label instanceof HTMLElement) label.style.display = 'block';
            }}
          />
          <span className="token-fallback">
            {(TOKEN_LABEL[token.kind] ?? token.kind).slice(0, 4)}
          </span>

          {/* How many are here, when it is more than one. */}
          {all.length > 1 && <span className="token-count">{all.length}</span>}

          {/* Goo wears out. The count is the token's, not the robot's, so
              it is right to draw it on the token: a robot destroyed in the
              goo leaves it worn to whatever number is showing. Top RIGHT,
              opposite the stack badge, so the two never collide. */}
          {token.kind === 'goo' && token.remaining !== undefined && (
            <span
              className="token-remaining"
              title={`${token.remaining} goo remaining`}
            >
              {token.remaining}
            </span>
          )}
        </div>
        );
      })}

      {/* A shot in flight: the squares BETWEEN shooter and target, drawn
          for the beat the hit lands on. Under the robots and over the
          tokens -- a beam passes across the floor and behind the machine
          that fired it, and drawing it on top put a line across the face
          of both robots. Nothing is drawn where the two are adjacent:
          there is no space between them to put it in. */}
      {/* A blast or an EMP: the device's token on every square it reached. */}
      {shot?.area && shot.area.map((square) => (
        <div
          key={`${square.x},${square.y}`}
          className="shot-beam"
          style={{
            left: square.x * cellPx,
            top: square.y * cellPx,
            width: cellPx,
            height: cellPx,
            backgroundImage: `url(${shot.art})`,
            backgroundSize: `${cellPx}px ${cellPx}px`,
          }}
          role="presentation"
        />
      ))}
      {shot?.tiles && shot.tiles.map((tile, i) => {
        // A flame is one image per square, so each is turned on its own
        // square rather than as a run. The nearest square is the far end
        // of the run when the flame goes north or west.
        const back = shot.towards === 'N' || shot.towards === 'W';
        const at = back ? shot.cells - 1 - i : i;
        const vertical = shot.towards === 'N' || shot.towards === 'S';
        const turn = FLAME_TURN[shot.towards] ?? 0;
        return (
          <div
            key={i}
            className="shot-beam"
            style={{
              left: (shot.x + (vertical ? 0 : at)) * cellPx,
              top: (shot.y + (vertical ? at : 0)) * cellPx,
              width: cellPx,
              height: cellPx,
              backgroundImage: `url(${tile})`,
              backgroundSize: `${cellPx}px ${cellPx}px`,
              transform: turn === 0 ? undefined : `rotate(${turn}deg)`,
            }}
            role="presentation"
          />
        );
      })}
      {shot && !shot.tiles && !shot.area && (() => {
        // ALWAYS laid out as a horizontal run, because the art is drawn
        // pointing east and CSS cannot turn a background image -- only the
        // element carrying it. Every other direction is that same run spun
        // about its own centre, which is what the board images above do and
        // for the same reason. Turned to the direction the shot TRAVELLED,
        // not merely to its axis: a laser and a beam read the same either
        // way round, and a shell flew nose first down half the board and
        // tail first down the other half.
        const turn = SHOT_TURN[shot.towards] ?? 0;
        const vertical = shot.towards === 'N' || shot.towards === 'S';
        const runPx = shot.cells * cellPx;
        const centreX = (shot.x + (vertical ? 0.5 : shot.cells / 2)) * cellPx;
        const centreY = (shot.y + (vertical ? shot.cells / 2 : 0.5)) * cellPx;
        return (
          <div
            className="shot-beam"
            style={{
              left: centreX - runPx / 2,
              top: centreY - cellPx / 2,
              width: runPx,
              height: cellPx,
              backgroundImage: `url(${shot.art})`,
              // Repeated, never stretched: one copy of the art per square,
              // so a three-square run is three tiles rather than one tile
              // pulled out of shape.
              backgroundSize: `${cellPx}px ${cellPx}px`,
              backgroundRepeat: 'repeat-x',
              transform: turn === 0 ? undefined : `rotate(${turn}deg)`,
            }}
            role="presentation"
          />
        );
      })()}

      {/* Archive markers: where each robot would come back. Drawn before
          the robots so a robot standing on its own archive sits on top of
          it rather than being hidden by it. Public information -- these
          are physical markers on the table in the board game. */}
      {robots
        .filter((r) => (
          r.archiveMarker
          && !r.eliminated
          // Not under its own robot: it says nothing there, and at the
          // start of a game every marker coincides with its robot, which
          // put a washed-out diamond under every one of them.
          && !(r.archiveMarker.x === r.x && r.archiveMarker.y === r.y && !r.destroyed)
        ))
        .map((robot) => (
          <div
            key={`archive-${robot.id}`}
            className={`archive${robot.id === youAre ? ' mine' : ''}`}
            style={{
              position: 'absolute',
              left: robot.archiveMarker!.x * cellPx,
              top: robot.archiveMarker!.y * cellPx,
              width: cellPx,
              height: cellPx,
            }}
            title={`${nameOf(robot.id)} respawns here`}
          >
            <svg viewBox="0 0 100 100" style={{ width: '100%', height: '100%' }}>
              <path
                d="M50 14 L86 50 L50 86 L14 50 Z"
                fill="none"
                stroke={colours.get(robot.id) ?? 'var(--paint-blue)'}
                strokeWidth="9"
                strokeLinejoin="round"
              />
              <path
                d="M50 14 L86 50 L50 86 L14 50 Z"
                fill="none"
                stroke="var(--ink)"
                strokeWidth="3"
                strokeLinejoin="round"
                opacity="0.65"
              />
            </svg>
          </div>
        ))}

      {/*
        Exactly one robot per square is drawn: the one the square's cycle
        is currently showing. Robots sharing a square are cycled through
        one at a time, and the count badge says how many are there, so a
        pile at the start of a dockless course can be read apart by
        clicking rather than by peering at a stagger.

        That replaced a small positional nudge for stacked robots. With
        only one drawn there is nothing to nudge apart, and offsetting a
        solitary robot only made it look misplaced.
      */}
      {robots
        .filter((r) => !r.destroyed)
        // Hidden while the square's cycle is showing something under it.
        // A robot covers its square as thoroughly as a token does, so it
        // takes its turn in the same cycle.
        .filter((r) => robotShown(r.id, r.x, r.y))
        .slice()
        .sort((a, b) => {
          if (a.id === youAre) return 1;
          if (b.id === youAre) return -1;
          // A virtual robot sits under a solid one: it cannot affect
          // anything, so it should not obscure something that can.
          if (Boolean(a.virtual) !== Boolean(b.virtual)) return a.virtual ? -1 : 1;
          return a.id.localeCompare(b.id);
        })
        .map((robot) => (
        <div
          // Remounted whenever `cut` changes, so the positions that come
          // with it are drawn rather than travelled to.
          key={`${cut ?? ''}:${robot.id}`}
          className={`robot${robot.id === youAre ? ' mine' : ''}`
            + `${robot.virtual ? ' virtual' : ''}`
            + `${wrecked?.has(robot.id) ? ' wrecked' : ''}`}
          style={{
            position: 'absolute',
            left: robot.x * cellPx,
            top: robot.y * cellPx,
            width: cellPx,
            height: cellPx,
            // Clicks fall through to the square's overlay underneath, so
            // clicking a robot cycles the square like clicking anything
            // else on it. Robots carry no click handler of their own.
            pointerEvents: 'none',
            // Movement is animated so a register playback reads as motion
            // rather than teleporting.
            transition: 'left 260ms ease, top 260ms ease',
          }}
          title={`${nameOf(robot.id)}, ${robot.damage} damage`}
        >
          <svg viewBox="0 0 100 100" style={{ width: '100%', height: '100%' }}>
            <g transform={`rotate(${FACING_ROTATION[robot.facing] ?? 0} 50 50)`}>
              {/* The nose is drawn FIRST, in the robot's own colour, so
                  the body rect below covers the seam where the two shapes
                  meet. It is FILLED solid rather than outlined, and in the
                  robot's colour rather than black: facing is the one thing
                  that must be legible at a glance, since it is what every
                  program card acts on, and a black nose reads as a hole
                  against dark board art. The body stays unfilled, so the
                  tile underneath it stays readable -- a solid robot hides
                  the very square whose contents decide what happens to it.
                  Both outlines are doubled, the robot's colour over a dark
                  backing stroke, so they hold up over pale floor and dark
                  art alike. */}
              <path
                d="M50 6 L74 30 L26 30 Z M26 30 L74 30"
                fill="none"
                stroke="var(--ink)" strokeWidth="11" strokeLinejoin="round"
                opacity="0.55"
              />
              <rect
                x="20" y="28" width="60" height="52" rx="8"
                fill="none"
                stroke="var(--ink)" strokeWidth="11"
                opacity="0.55"
              />
              <path
                d="M50 6 L74 30 L26 30 Z"
                fill={colours.get(robot.id) ?? 'var(--paint-blue)'}
                stroke={colours.get(robot.id) ?? 'var(--paint-blue)'}
                strokeWidth="7" strokeLinejoin="round"
              />
              <rect
                x="20" y="28" width="60" height="52" rx="8"
                fill="none"
                stroke={colours.get(robot.id) ?? 'var(--paint-blue)'}
                strokeWidth="7"
              />
              {/* The last flag tagged, in the middle of the body. Placed
                  in the robot's own frame so it sits in the body whichever
                  way the robot faces, and turned back so it always reads
                  upright. White over a dark outline, for pale floor and
                  dark art alike; red for a bot. Nothing until the first
                  flag. */}
              {(robot.lastTouchedFlag ?? 0) > 0 && (
                <text
                  x="50" y="54"
                  transform={`rotate(${-(FACING_ROTATION[robot.facing] ?? 0)} 50 54)`}
                  textAnchor="middle" dominantBaseline="central"
                  fontSize="30" fontWeight="700"
                  fill={bots?.has(robot.id) ? '#ff3b30' : '#fff'}
                  stroke="var(--ink)" strokeWidth="5"
                  paintOrder="stroke"
                >
                  {robot.lastTouchedFlag}
                </text>
              )}
            </g>

            {/* Struck through, OUTSIDE the rotation group so the cross
                always reads as a cross rather than turning with the
                robot. */}
            {wrecked?.has(robot.id) && (
              <>
                {/* Light over dark rather than red: a red cross on a red
                    robot was unreadable, and the cross must work over
                    every player colour. */}
                <path
                  d="M14 14 L86 86 M86 14 L14 86"
                  stroke="var(--ink)" strokeWidth="16" strokeLinecap="round"
                  opacity="0.75"
                />
                <path
                  d="M14 14 L86 86 M86 14 L14 86"
                  stroke="#fff" strokeWidth="7" strokeLinecap="round"
                />
              </>
            )}
          </svg>
          {robots.filter((r) => !r.destroyed && r.x === robot.x && r.y === robot.y).length > 1
            && robot.id === youAre && (
            <span className="robot-badge stack">
              {robots.filter((r) => !r.destroyed && r.x === robot.x && r.y === robot.y).length}
            </span>
          )}
          {/* Virtual is marked with a badge rather than by drawing the
              robot differently: a dashed outline was hard to read at cell
              size, and each badge has its own corner so several can show
              at once. */}
          {robot.virtual && <span className="robot-badge virtual-badge">V</span>}
          {robot.poweredDown && <span className="robot-badge down">off</span>}
          {robot.damage > 0 && <span className="robot-badge dmg">{robot.damage}</span>}
        </div>
      ))}
    </div>
  );
}
